import React from "react";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { useManageIPMSContext } from "./ManageIPMSContex";
import moment from "moment";
import { getCookieData } from "../../helper/api";
import { GoalsAssessmentFlagStatusEnumTextCap } from "./constant";
import ReactDataTable from "./ReactDataTable";
import { useNavigate } from "react-router-dom";
import useIPMSApi from "./ipms";
import { GetEmpGoalAssessmentBymanagerResponse } from "./newipmsmodel";
import SingleSelect from "../../components/SingleSelect";

const STORAGE_KEY = "employeeGoalAssessmentFilters";

const getSavedFilters = () => {
  try {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.error("Error reading saved filters:", e);
  }
  return null;
};

const EmployeeGoalAssessmentReviewPage: React.FC = () => {
  const { assessmentData } = useManageIPMSContext();
  const { fetchGetIpmsGetEmpAssessmentByManager } = useIPMSApi();
  const navigate = useNavigate();
  const currentUser = getCookieData("userData");
  const userDetail = currentUser && JSON.parse(currentUser);

  const savedState = React.useMemo(() => getSavedFilters(), []);

  const [activeSearchTerm, setActiveSearchTerm] = React.useState<string>(
    savedState?.activeSearchTerm ?? ""
  );
  const [activeStatusFilter, setActiveStatusFilter] = React.useState<string>(
    savedState?.activeStatusFilter ?? "SUBMITTED"
  );
  const [currentPage, setCurrentPage] = React.useState<number>(
    savedState?.currentPage ?? 1
  );
  const [pageSize, setPageSize] = React.useState<number>(
    savedState?.pageSize ?? 10
  );
  const [activeSortBy, setActiveSortBy] = React.useState<string>(
    savedState?.activeSortBy ?? "employee_name"
  );
  const [activeSortOrder, setActiveSortOrder] = React.useState<string>(
    savedState?.activeSortOrder ?? "asc"
  );

  const [pendingSortBy, setPendingSortBy] = React.useState<string>(
    savedState?.pendingSortBy ?? "employee_name"
  );
  const [pendingSortOrder, setPendingSortOrder] = React.useState<string>(
    savedState?.pendingSortOrder ?? "asc"
  );

  React.useEffect(() => {
    const filtersToSave = {
      activeSearchTerm,
      activeStatusFilter,
      currentPage,
      pageSize,
      activeSortBy,
      activeSortOrder,
      pendingSortBy,
      pendingSortOrder,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filtersToSave));
  }, [
    activeSearchTerm,
    activeStatusFilter,
    currentPage,
    pageSize,
    activeSortBy,
    activeSortOrder,
    pendingSortBy,
    pendingSortOrder,
  ]);

  const assessmentYear = React.useMemo(() => {
    return assessmentData?.assessment_periods?.[0]
      ? moment(
          assessmentData.assessment_periods[0].start_assessment_date,
        ).year()
      : moment().year();
  }, [assessmentData]);

  const { data: goalData, isLoading } = useQuery({
    queryKey: [
      "managerGoals",
      userDetail?.employee_id,
      assessmentYear,
      activeSearchTerm,
      activeStatusFilter,
      currentPage,
      pageSize,
      activeSortBy,
      activeSortOrder,
    ],
    queryFn: () =>
      fetchGetIpmsGetEmpAssessmentByManager({
        employee_id: userDetail?.employee_id,
        assessment_year: 0,
        search_term: activeSearchTerm,
        department_id: 0,
        list_type: "ASSESSMENT",
        status_filter: activeStatusFilter,
        sort_by: activeSortBy,
        sort_order: activeSortOrder,
        page: currentPage,
        page_size: pageSize,
      }),
    enabled: !!userDetail?.employee_id && !!assessmentYear,
  });

  React.useEffect(() => {
    if (goalData?.pagination?.total !== undefined) {
      const total = goalData.pagination.total;
      const maxPage = Math.max(1, Math.ceil(total / pageSize));
      if (currentPage > maxPage) {
        setCurrentPage(maxPage);
      }
    }
  }, [goalData, currentPage, pageSize]);

  const columns = [
    {
      name: "EmpName",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
        <span className=" text-wrap">{row.full_name}</span>
      ),
      sortable: true,
      width: "150px",
    },
    {
      name: "EmpId",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
        <span className="text-wrap">{row.employee_id}</span>
      ),
      sortable: true,
      // center: true,
      width: "100px",
    },
    {
      name: "Manager",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
        <span className="text-wrap">{row.manager_name}</span>
      ),
      sortable: true,
      width: "150px",
    },
    // {
    //   name: "Department",
    //   selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
    //     <span className=" text-wrap pl-10 block">{row.department}</span>
    //   ),
    //   sortable: false,
    //   width: "200px",
    // },
    {
      name: "DOJ",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => row.doj,
      sortable: false,
      center: true,
      width: "120px",
    },
    // {
    //   name: "Initiation Date",
    //   selector: (row: GetEmpGoalAssessmentBymanagerResponse) =>
    //     row.yearstartdate,
    //   sortable: true,
    // },
    {
      name: "Quarter Dates",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
        <span className="text-wrap">
          {row.period_start_date && row.period_end_date
            ? `${moment(row.period_start_date).format("DD MMM YYYY")} - ${moment(row.period_end_date).format("DD MMM YYYY")}`
            : ""}
        </span>
      ),
      sortable: true,
      center: true,
      width: "200px",
    },
    {
      name: "Submission On",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) =>
        moment(row.assessment_submitted_at).isValid()
          ? moment(row.assessment_submitted_at).format("DD MMM YYYY")
          : "",
      sortable: true,
      center: true,
      width: "150px",
      sortFunction: (
        rowA: GetEmpGoalAssessmentBymanagerResponse,
        rowB: GetEmpGoalAssessmentBymanagerResponse,
      ) => {
        const dateA = rowA.assessment_submitted_at
          ? new Date(rowA.assessment_submitted_at).getTime()
          : 0;
        const dateB = rowB.assessment_submitted_at
          ? new Date(rowB.assessment_submitted_at).getTime()
          : 0;

        if (dateA > dateB) {
          return 1;
        }
        if (dateA < dateB) {
          return -1;
        }
        return 0;
      },
    },
    {
      name: "Approved On",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) =>
        moment(row.assessment_approved_at).isValid()
          ? moment(row.assessment_approved_at).format("DD MMM YYYY")
          : "",
      sortable: true,
      center: true,
      width: "120px",
      sortFunction: (
        rowA: GetEmpGoalAssessmentBymanagerResponse,
        rowB: GetEmpGoalAssessmentBymanagerResponse,
      ) => {
        const dateA = rowA.assessment_approved_at
          ? new Date(rowA.assessment_approved_at).getTime()
          : 0;
        const dateB = rowB.assessment_approved_at
          ? new Date(rowB.assessment_approved_at).getTime()
          : 0;

        if (dateA > dateB) {
          return 1;
        }
        if (dateA < dateB) {
          return -1;
        }
        return 0;
      },
    },
    {
      name: "Quarter",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => row.quarter,
      sortable: true,
      center: true,
      width: "90px",
    },
    // {
    //   name: "Duration",
    //   selector: (row: GetEmpGoalAssessmentBymanagerResponse) => (
    //     <span className=" text-wrap">
    //       {row.duration
    //         ? moment(row.duration.split(" to ")[0]).format("DD MMM YYYY")
    //         : ""}{" "}
    //       - <br />{" "}
    //       {row.duration
    //         ? moment(row.duration.split(" to ")[1]).format("DD MMM YYYY")
    //         : ""}
    //     </span>
    //   ),
    //   sortable: true,
    //   sortFunction: (
    //     rowA: GetEmpGoalAssessmentBymanagerResponse,
    //     rowB: GetEmpGoalAssessmentBymanagerResponse
    //   ) => {
    //     const extractStartDate = (
    //       durationString: string | null | undefined
    //     ) => {
    //       if (!durationString) {
    //         return null; // Return null if the duration string is null or undefined
    //       }
    //       const parts = durationString.split(" to ");
    //       return parts[0]; // Gets "YYYY-MM-DD"
    //     };

    //     const startDateStringA = extractStartDate(rowA.duration);
    //     const startDateStringB = extractStartDate(rowB.duration);

    //     // Handle potential null dates after extraction
    //     const dateA = startDateStringA
    //       ? new Date(startDateStringA).getTime()
    //       : -Infinity;
    //     const dateB = startDateStringB
    //       ? new Date(startDateStringB).getTime()
    //       : -Infinity;

    //     if (dateA > dateB) {
    //       return 1;
    //     }
    //     if (dateA < dateB) {
    //       return -1;
    //     }
    //     return 0;
    //   },
    // },
    {
      name: "Status",
      selector: (row: GetEmpGoalAssessmentBymanagerResponse) => {
        const assessmentStatus = (row.assessment_status || "").toUpperCase();
        const statusText =
          GoalsAssessmentFlagStatusEnumTextCap[assessmentStatus] ||
          assessmentStatus;

        const getStatusClasses = () => {
          if (statusText === "DRAFT" || statusText === "READY") {
            return "bg-gray-100 text-gray-800 dark:text-gray-400 border-gray-500";
          }
          if (statusText === "SUBMITTED") {
            return "bg-blue-100 text-blue-800 dark:text-blue-400 border-blue-400";
          }
          if (statusText === "APPROVED") {
            return "bg-yellow-100 text-yellow-800 dark:text-yellow-300 border-yellow-300";
          }
          if (statusText === "COMPLETED") {
            return "bg-green-100 text-green-800 dark:text-green-400 border-green-400";
          }
          return "bg-gray-100 text-gray-800";
        };

        return (
          <span
            className={`${getStatusClasses()} border-0 text-xs font-medium px-2.5 py-0.5 rounded dark:bg-gray-700`}
          >
            {statusText === "READY" ? "SAVED" : statusText}
          </span>
        );
      },
      sortable: true,
      sortFunction: (
        rowA: GetEmpGoalAssessmentBymanagerResponse,
        rowB: GetEmpGoalAssessmentBymanagerResponse,
      ) => {
        const statusA = rowA.goal_status;
        const statusB = rowB.goal_status;

        // Get the numerical priority for each status
        // Handle cases where submissionstatus might not be a valid enum key or is null/undefined
        const priorityA =
          GoalsAssessmentFlagStatusEnumTextCap[statusA] !== undefined
            ? GoalsAssessmentFlagStatusEnumTextCap[statusA]
            : Infinity; // Assign high priority for unknown/null statuses
        const priorityB =
          GoalsAssessmentFlagStatusEnumTextCap[statusB] !== undefined
            ? GoalsAssessmentFlagStatusEnumTextCap[statusB]
            : Infinity; // Assign high priority for unknown/null statuses

        if (priorityA > priorityB) {
          return 1;
        }
        if (priorityA < priorityB) {
          return -1;
        }
        return 0; // statuses have the same priority
      },
      width: "50",
      center: true,
    },
  ];

  const goalListData = React.useMemo(() => {
    return goalData?.items
      ? goalData?.items.map((item) => {
          return {
            ...item,
            doj: moment(item["doj"]).isValid()
              ? moment(item["doj"]).format("DD MMM YYYY")
              : null,
          };
        })
      : null;
  }, [goalData]);

  // React.useEffect(() => {
  //   GetEmployeeGoalFinalAssessmentByManager({
  //     employee_id: userDetail?.employee_id,
  //     assessment_year: 2025,
  //     search_term: "",
  //     page_number: 1,
  //     page_size: 10,
  //   });
  // }, []);

  const getSameDateThisYear = (dateString: string, year: number): string => {
    const [day, monthName] = dateString.split(" ");
    const month = new Date(Date.parse(monthName + " 1, 2020")).getMonth(); // Get month index

    // Create a new date object with the provided year
    const newDate = new Date(year, month, parseInt(day, 10));

    // Format the new date as 'dd MMMM yyyy'
    return format(newDate, "yyyy-MM-dd");
  };

  const handleEdit = (item: GetEmpGoalAssessmentBymanagerResponse) => {
    if (item.doj) {
      const originalDate = moment(item.doj, "DD MMM YYYY");
      const yearstart = item.year;
      const updatedDate = originalDate.clone().year(yearstart);
      // const startDate = updatedDate.clone();
      const endDate = updatedDate.clone().add(1, "year");
      // const startDate = updatedDate.clone();
      // const endDate = updatedDate.clone().add(1, "year");

      // const checkEndDate = endDate && moment(new Date(endDate), "YYYY-MM-DD");
      // const end = checkEndDate && checkEndDate.clone().add(1, "year");
      // console.log('state?.submissionstatus', item.submissionstatus)
      navigate("/ipms_new/goalassessment", {
        state: {
          id: item.quarter,
          quarterName: item.quarter,
          employeeID: item.employee_id,
          employeeName: item.full_name,
          formType: "ManageInput",
          submissionstatus: item.goal_status,
          assessmentData: {
            start: getSameDateThisYear(item.doj, item.year),
            end: moment(endDate).format("YYYY-MM-DD"),
          },
        },
      });
    }
  };

  const statusOptions = [
    { value: "SUBMITTED", label: "Employee Submitted" },
    { value: "APPROVED", label: "Manager Approved" },
    { value: "COMPLETED", label: "HR Approved" },
    { value: "READY", label: "Employee Saved" },
    { value: "ALL", label: "All Statuses" },
  ];

  const handleClearFilters = () => {
    setActiveSearchTerm("");
    setActiveStatusFilter("SUBMITTED");
    setCurrentPage(1);
    setActiveSortBy("employee_name");
    setActiveSortOrder("asc");
    setPendingSortBy("employee_name");
    setPendingSortOrder("asc");
    sessionStorage.removeItem(STORAGE_KEY);
  };

  // return <IPMSViewEmployeeTable columns={columns} data={goalListData ?? []} />;
  return (
    <>
      <span className="bg-gray-100 text-gray-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray-700 dark:text-gray-400 border border-gray-500">
        Employee Saved
      </span>
      <span className="bg-blue-100 text-blue-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray-700 dark:text-blue-400 border border-blue-400">
        Employee Submitted
      </span>
      <span className="bg-yellow-100 text-yellow-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray-700 dark:text-yellow-300 border border-yellow-300">
        Manager Approved
      </span>
      <span className="bg-green-100 text-green-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-gray-700 dark:text-green-400 border border-green-400">
        HR Approved
      </span>
      <p className="text-gray-500 text-sm mt-3">
        Note: Action buttons are only available when the date of join is
        present.
      </p>
      <ReactDataTable
        columns={columns}
        data={goalListData ?? []}
        onView={handleEdit}
        dateOfJoinAction={"doj"}
        headerColor={"green"}
        searchLabelName="Search By Employee Name, Employee ID"
        showFilter={true}
        paginationServer={true}
        paginationTotalRows={goalData?.pagination?.total ?? 0}
        onChangePage={(page) => setCurrentPage(page)}
        onChangeRowsPerPage={(size) => setPageSize(size)}
        onSearch={(text) => {
          setActiveSearchTerm(text);
          setActiveSortBy(pendingSortBy);
          setActiveSortOrder(pendingSortOrder);
          setCurrentPage(1);
        }}
        useSearchButton={true}
        initialSearchValue={activeSearchTerm}
        progressPending={isLoading}
        extraFilters={
          <div className="flex gap-3 items-end">
            <div className="flex flex-col">
              <label className="block text-sm mb-2 font-medium leading-6 text-gray-900 dark:text-gray-400">
                Status
              </label>
              <SingleSelect
                value={
                  statusOptions.find((opt) => opt.value === activeStatusFilter) || {
                    value: "SUBMITTED",
                    label: "Employee Submitted",
                  }
                }
                onChange={(option: any) => {
                  setActiveStatusFilter(option?.value || "SUBMITTED");
                  setCurrentPage(1);
                }}
                options={statusOptions}
                placeholder="Status"
                className="mt-0 w-[180px]"
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-sm mb-2 font-medium leading-6 text-gray-900 dark:text-gray-400">
                Sort By
              </label>
              <SingleSelect
                value={{
                  label:
                    pendingSortBy === "employee_id"
                      ? "Employee ID"
                      : pendingSortBy === "employee_name"
                        ? "Employee Name"
                        : pendingSortBy === "department"
                          ? "Department"
                          : pendingSortBy === "doj"
                            ? "Date of Joining"
                            : pendingSortBy === "quarter"
                              ? "Quarter"
                              : pendingSortBy === "quater_dates"
                                ? "Quarter Dates"
                                : pendingSortBy === "assessment_submitted_at"
                                  ? "Submission Date"
                                  : "Status",
                  value: pendingSortBy,
                }}
                onChange={(option: any) => setPendingSortBy(option?.value)}
                options={[
                  { value: "employee_id", label: "Employee ID" },
                  { value: "employee_name", label: "Employee Name" },
                  // { value: "department", label: "Department" },
                  { value: "doj", label: "Date of Joining" },
                  { value: "quarter", label: "Quarter" },
                  { value: "quater_dates", label: "Quarter Dates" },
                  {
                    value: "assessment_submitted_at",
                    label: "Submission Date",
                  },
                  { value: "assessment_status", label: "Status" },
                ]}
                placeholder="Sort By"
                className="mt-0 w-[200px]"
              />
            </div>
            <div className="flex flex-col">
              <label className="block text-sm mb-2 font-medium leading-6 text-gray-900 dark:text-gray-400">
                Order
              </label>
              <SingleSelect
                value={{
                  label: pendingSortOrder === "asc" ? "Asc" : "Desc",
                  value: pendingSortOrder,
                }}
                onChange={(option: any) => setPendingSortOrder(option?.value)}
                options={[
                  { value: "asc", label: "Asc" },
                  { value: "desc", label: "Desc" },
                ]}
                placeholder="Order"
                className="mt-0 w-[100px]"
              />
            </div>
            <button
              type="button"
              onClick={handleClearFilters}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 mb-0.5"
            >
              Clear
            </button>
          </div>
        }
      />
    </>
  );
};

export default EmployeeGoalAssessmentReviewPage;
